import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbRecaptchaViewStore } from "mrb/components";
import { RegisterForm } from "public/membership/forms";
import { StripeField } from "common/models";
import { currencyFormatter } from "mrb/common/formatting";
import { applicationErrorCodes, subscriptionPlans as subscriptionPlansConstant } from "common/constants";
import moment from "moment";
import MobxReactFormDevTools from "mobx-react-form-devtools";
import ReactGA from "react-ga4";

class RegisterViewStore extends MrbBaseViewStore {
    @observable success = false;
    // Subscription is mandatory at sign-up, so the subscribe flow is always active.
    @observable isSubscribing = true;
    // Drives the submit button's busy state on the subscribe path, which bypasses
    // form.onSubmit() and therefore never sets form.submitting.
    @observable isProcessing = false;
    // Submit/payment error shown in the footer WITHOUT going through form.invalidate, so it never
    // affects form.isValid. (The gated submit button reads form.isValid; routing this error through
    // the form would keep the button disabled until a form field changes, and the Stripe card field
    // is not a form field, so editing the card would never clear it.)
    @observable submitErrorMessage = null;
    @observable showCouponField = false;
    @observable couponCode = "";
    @observable.ref appliedCoupon = null;
    @observable couponErrorCode = null;
    @observable isApplyingCoupon = false;
    @observable selectedPlanId = null;
    @observable.ref subscriptionPlans = [];
    @observable.ref stripe = null;
    stripeField = null;

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;
        this.stripeField = new StripeField();

        this.form = new RegisterForm({
            onSuccess: (form) => {
                if (this.appliedCoupon) {
                    // The subscribe path is triggered from the template's submit
                    // handler with the mounted cardElement.
                    return;
                }
                const data = form.values();
                return this.register(data);
            },
            onInit: (form) => {
                form.$("firstName").focus();
            },
        });

        this.recaptchaViewStore = new MrbRecaptchaViewStore(this.rootStore, {
            siteKey: ApplicationSettings.recaptchaSiteKey,
        });

        if (process.env.NODE_ENV === "development") {
            MobxReactFormDevTools.register({ register: this.form });
            MobxReactFormDevTools.select("register");
        }
    }

    async onInit() {
        return this.fetchSubscriptionPlans();
    }

    async fetchSubscriptionPlans() {
        try {
            const plans = await this.routeStore.getSubscriptionPlans();
            runInAction(() => {
                this.subscriptionPlans = plans;
                if (!this.selectedPlanId) {
                    this.selectedPlanId = this.getDefaultPlanId(plans);
                }
            });
        } catch (err) {
            this.rootStore.notificationStore.error(
                "Unexpected error occurred while loading subscription plans. Please contact the support team.",
                err
            );
        }
    }

    // Default the plan picker to Naki One, falling back to the first available plan.
    getDefaultPlanId(plans) {
        if (!plans || plans.length === 0) {
            return null;
        }
        const one = plans.find((plan) => plan.slug === subscriptionPlansConstant.nakiOne);
        return (one || plans[0]).id;
    }

    @action.bound
    setShowCouponField(value) {
        this.showCouponField = value;
        if (!value) {
            this.couponCode = "";
            this.couponErrorCode = null;
        }
    }

    @action.bound
    onChangeCouponCode(value) {
        this.couponCode = value;
        this.couponErrorCode = null;
    }

    @action.bound
    async applyCoupon() {
        const code = (this.couponCode || "").trim();
        if (!code) {
            return;
        }
        this.isApplyingCoupon = true;
        this.couponErrorCode = null;
        try {
            const result = await this.routeStore.validateSignupPromoCode(code);
            runInAction(() => {
                this.appliedCoupon = result;
                this.couponCode = result.promoCode;
            });
        } catch (err) {
            runInAction(() => {
                this.appliedCoupon = null;
                this.couponErrorCode = err && err.data ? err.data.errorCode : null;
            });
        } finally {
            runInAction(() => {
                this.isApplyingCoupon = false;
            });
        }
    }

    @action.bound
    removeCoupon() {
        this.appliedCoupon = null;
        this.couponErrorCode = null;
        this.couponCode = "";
        this.showCouponField = false;
    }

    @action.bound
    setSelectedPlan(planId) {
        this.selectedPlanId = planId;
    }

    @action.bound
    setStripe(stripe) {
        this.stripe = stripe;
    }

    @computed
    get couponErrorMessageKey() {
        switch (this.couponErrorCode) {
            case applicationErrorCodes.payment.promoCodeExpired:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.EXPIRED";
            case applicationErrorCodes.payment.promoCodeRedemptionLimitReached:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.LIMIT_REACHED";
            case applicationErrorCodes.payment.promoCodeRestrictionNotMet:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.RESTRICTION";
            case applicationErrorCodes.payment.promoCodeNotFound:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NOT_VALID";
            case applicationErrorCodes.payment.promoCodeAlreadyRedeemed:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.ALREADY_USED";
            case applicationErrorCodes.payment.promoCodeNewCustomersOnly:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NEW_CUSTOMERS_ONLY";
            case null:
            case undefined:
                return null;
            default:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.GENERIC";
        }
    }

    formatPrice(value) {
        return currencyFormatter.format(value, { currency: "USD" });
    }

    @computed
    get isSubscribeDisabled() {
        if (!this.isSubscribing) {
            return false;
        }
        return !this.selectedPlanId || !this.stripe || !this.stripeField || !this.stripeField.isCompleted;
    }

    // When the user is subscribing and has picked a plan, describe when the first charge happens.
    // A 100%-off coupon defers the first charge by its free months; otherwise the card is charged today.
    @computed
    get firstPaymentMessage() {
        if (!this.isSubscribing || !this.selectedPlan) {
            return null;
        }
        let freeMonths = 0;
        if (this.appliedCoupon) {
            const { percentOff, duration, durationInMonths } = this.appliedCoupon;
            if (percentOff === 100) {
                if (duration === "forever") {
                    return { key: "REGISTER.SUBSCRIBE.FIRST_PAYMENT_FREE_FOREVER", params: {} };
                }
                freeMonths = duration === "repeating" ? durationInMonths || 1 : 1;
            }
        }
        if (freeMonths > 0) {
            const date = moment().add(freeMonths, "months").format("MMMM D, YYYY");
            return { key: "REGISTER.SUBSCRIBE.FIRST_PAYMENT_ON", params: { date } };
        }
        return { key: "REGISTER.SUBSCRIBE.FIRST_PAYMENT_TODAY", params: {} };
    }

    @computed
    get selectedPlan() {
        if (!this.selectedPlanId) {
            return null;
        }
        return this.subscriptionPlans.find((plan) => plan.id === this.selectedPlanId) || null;
    }

    // True when the first invoice will be $0. Stripe marks a $0 invoice paid at creation, so the
    // subscription becomes active before any card exists -- which is why a failed card step used to
    // leave a permanently-active orphan and burn a coupon redemption. When this is true we collect the
    // card BEFORE creating the subscription (see submitWithSubscription).
    //
    // A wrong prediction is harmless by design: if an amount turns out to be due, preprocess returns a
    // clientSecret and we confirm the charge exactly as before, just with the card already saved.
    @computed
    get isFirstInvoiceFree() {
        if (!this.appliedCoupon || !this.selectedPlan) {
            return false;
        }
        const { percentOff, amountOff } = this.appliedCoupon;
        if (percentOff === 100) {
            return true;
        }
        return !!amountOff && amountOff >= this.selectedPlan.price;
    }

    @computed
    get discountSummary() {
        if (!this.appliedCoupon || !this.selectedPlan) {
            return null;
        }
        const price = this.selectedPlan.price;
        const { percentOff, amountOff, duration, durationInMonths } = this.appliedCoupon;
        const months = durationInMonths || 1;

        if (percentOff === 100) {
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_FOREVER", params: {} };
            }
            const m = duration === "repeating" ? months : 1;
            const key =
                m === 1
                    ? "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_ONE_MONTH"
                    : "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.FREE_MONTHS";
            return { key, params: { months: m, price: this.formatPrice(price) } };
        }

        if (typeof percentOff === "number" && percentOff > 0) {
            const discounted = Math.max(Math.round(price * (1 - percentOff / 100) * 100) / 100, 0);
            const base = {
                percent: percentOff,
                price: this.formatPrice(price),
                discountedPrice: this.formatPrice(discounted),
            };
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_FOREVER", params: base };
            }
            if (duration === "repeating") {
                return {
                    key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_REPEATING",
                    params: { ...base, months },
                };
            }
            return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.PERCENT_ONCE", params: base };
        }

        if (typeof amountOff === "number" && amountOff > 0) {
            const discounted = Math.max(Math.round((price - amountOff) * 100) / 100, 0);
            const base = {
                amount: this.formatPrice(amountOff),
                price: this.formatPrice(price),
                discountedPrice: this.formatPrice(discounted),
            };
            if (duration === "forever") {
                return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_FOREVER", params: base };
            }
            if (duration === "repeating") {
                return {
                    key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_REPEATING",
                    params: { ...base, months },
                };
            }
            return { key: "APPLICATION.PREMIUM.PAYMENT.COUPON.SUMMARY.AMOUNT_ONCE", params: base };
        }

        return null;
    }

    @action register = async (data) => {
        try {
            const recaptchaToken = await this.recaptchaViewStore.execute();
            data.recaptcha = recaptchaToken;
            await this.routeStore.register(data);
            runInAction(() => {
                this.success = true;
            });
        } catch (err) {
            this.form.invalidate(this.mapRegisterError(err));
        }
    };

    mapRegisterError(err) {
        let message = "REGISTER.ERROR.GENERIC_MESSAGE";
        if (err && err.data) {
            // The subscribe-at-sign-up path returns a structured ActionResult error (err.data.errorCode);
            // a duplicate email there surfaces as the EmailAlreadyTaken code rather than a "DuplicateEmail" string.
            if (err.data.errorCode === applicationErrorCodes.payment.emailAlreadyTaken) {
                message = "REGISTER.ERROR.DUPLICATE_EMAIL_MESSAGE";
            } else if (err.data === "DuplicateUserName") {
                message = "REGISTER.ERROR.DUPLICATE_USERNAME_MESSAGE";
            } else if (err.data === "DuplicateEmail") {
                message = "REGISTER.ERROR.DUPLICATE_EMAIL_MESSAGE";
            } else if (err.data === "Failed challenge request.") {
                message = "REGISTER.ERROR.RECAPTCHA_VERIFICATION_FAILED";
            }
        } else if (err && (err.type === "card_error" || err.type === "validation_error")) {
            // Stripe card decline / validation error. The specific reason is already shown on the
            // card field (stripeField.setError); show a clear payment message in the form banner
            // instead of the misleading generic "unexpected error".
            message = "REGISTER.ERROR.PAYMENT_FAILED";
        }
        return message;
    }

    @action.bound
    async submitWithSubscription(cardElement) {
        // Synchronous re-entrancy guard. The submit button's `disabled` binding reads isProcessing
        // (RegisterTemplate.jsx), but validation below is async (DVR debounce), so setting the flag
        // only after the first `await` leaves a window in which a second click starts an entirely
        // independent register + subscribe chain and the user ends up with two subscriptions.
        // See Redmine #158711.
        if (this.isProcessing) {
            return;
        }
        this.isProcessing = true;
        this.submitErrorMessage = null;

        // Everything below runs in one try/finally so isProcessing is released on every exit —
        // early return, thrown validator, or completed subscribe — because releasing it
        // per-branch is how the latch bug gets reintroduced.
        try {
            const validation = await this.form.validate({ showErrors: true });
            if (!validation.isValid) {
                return;
            }
            if (!this.selectedPlanId) {
                runInAction(() => {
                    this.submitErrorMessage = "REGISTER.ERROR.SELECT_PLAN";
                });
                return;
            }
            if (!cardElement || !this.stripeField.isCompleted) {
                runInAction(() => {
                    this.submitErrorMessage = "REGISTER.ERROR.CARD_REQUIRED";
                });
                return;
            }

            const { firstName, lastName, email, password, confirmPassword } = this.form.values();

            let registered = false;
            let signupToken = null;
            // Tracked for the rollback below: which Stripe subscription this attempt created, and whether a
            // real (non-zero) charge settled against it.
            let subscriptionId = null;
            let chargeSettled = false;
            try {
                const recaptcha = await this.recaptchaViewStore.execute();

                // 1) Create the account; the backend returns a scoped Bearer token we use
                //    for the subscribe + rollback-delete calls WITHOUT storing it in the SDK.
                signupToken = await this.routeStore.registerAccount({
                    firstName,
                    lastName,
                    email,
                    password,
                    confirmPassword,
                    recaptcha,
                });
                registered = true;

                // 2) Subscribe via the existing take-payment flow, threading the signup token
                //    as a per-request Authorization header so no session is opened yet.
                this.stripeField.resetValidation();

                let paymentMethod = null;

                // Card-first order for a $0 first invoice. Stripe marks a $0 invoice paid at creation, so
                // creating the subscription first makes it active before any card exists -- and a failed card
                // step then leaves a permanently-active orphan plus a burned coupon redemption. Confirming the
                // card first means a failure leaves nothing billable behind: no subscription, no invoice and no
                // coupon redemption. (An unused Stripe customer and SetupIntent do remain.)
                if (this.isFirstInvoiceFree) {
                    const setup = await this.routeStore.subscribeSetupPaymentMethod(signupToken);
                    const setupPayload = await this.stripe.confirmCardSetup(setup.token, {
                        payment_method: { card: cardElement },
                    });
                    if (setupPayload.error) {
                        this.stripeField.setError(setupPayload.error);
                        throw setupPayload.error;
                    }
                    paymentMethod = setupPayload.setupIntent.payment_method;
                }

                const pre = await this.routeStore.subscribePreprocess(
                    this.selectedPlanId,
                    this.appliedCoupon ? this.appliedCoupon.promoCode : null,
                    signupToken,
                    paymentMethod
                );
                subscriptionId = pre.paymentProviderSubscriptionId;

                if (pre.clientSecret) {
                    // An amount is due: no coupon, a partial one, or our $0 prediction was wrong. Confirm the
                    // charge as before. When the card was already saved above we pass its id, which avoids
                    // re-collecting it and is usually frictionless.
                    const payload = await this.stripe.confirmCardPayment(pre.clientSecret, {
                        payment_method: paymentMethod || { card: cardElement },
                        receipt_email: email,
                    });
                    if (payload.error) {
                        this.stripeField.setError(payload.error);
                        throw payload.error;
                    }
                    chargeSettled = true;
                } else if (!paymentMethod) {
                    // Nothing due and we did not predict it, so no card has been saved yet: original order.
                    const setup = await this.routeStore.subscribeSetupPaymentMethod(signupToken);
                    const setupPayload = await this.stripe.confirmCardSetup(setup.token, {
                        payment_method: { card: cardElement },
                    });
                    if (setupPayload.error) {
                        this.stripeField.setError(setupPayload.error);
                        throw setupPayload.error;
                    }
                    paymentMethod = setupPayload.setupIntent.payment_method;
                }

                await this.routeStore.subscribePostprocess(
                    {
                        paymentProviderSubscriptionId: subscriptionId,
                        paymentProviderPaymentMethodId: paymentMethod,
                    },
                    signupToken
                );
            } catch (err) {
                // Failure before subscription was confirmed: roll back the account so the user can try again.
                // The token is never stored on failure, so the user remains signed out.
                //
                // Cancel the Stripe subscription FIRST. Deleting the account does no Stripe cleanup, and a
                // 100%-off coupon produces a $0 invoice that Stripe marks paid at creation -- so the
                // subscription is already `active` and, unlike an unpaid one, will never expire on its own.
                // Skipping this is what left orphaned "duplicate" subscriptions in the dashboard with no
                // account attached. Skipped when a real charge settled: cancelling then would keep the
                // customer's money and leave them with nothing, which is worse than an orphan.
                if (subscriptionId && !chargeSettled) {
                    try {
                        await this.routeStore.subscribePostprocess(
                            { undoChanges: true, paymentProviderSubscriptionId: subscriptionId },
                            signupToken
                        );
                    } catch (undoErr) {
                        // best-effort; an unpaid subscription still expires on its own after ~23h
                    }
                }
                if (registered) {
                    try {
                        await this.routeStore.deleteAccount(signupToken);
                    } catch (deleteErr) {
                        // best-effort; the delete endpoint is guarded server-side
                    }
                }
                runInAction(() => {
                    this.submitErrorMessage = this.mapRegisterError(err);
                });
                return;
            }

            // 3) Payment confirmed. Store the token and sign in only now, outside the rollback try
            //    so a router/navigation error never triggers deleteAccount on a paid subscriber.
            try {
                await this.routeStore.finalizeLogin(signupToken);
                ReactGA.event("sign_up");
            } catch (err) {
                // The user is authenticated and has an active subscription; do not roll back.
                // Log and swallow so the outer finally releases isProcessing.
                this.rootStore.notificationStore.error("REGISTER.ERROR.GENERIC_MESSAGE", err);
            }
        } finally {
            runInAction(() => {
                this.isProcessing = false;
            });
        }
    }

    destroy() {
        super.destroy();
        this.form.destroy();
    }
}

export default RegisterViewStore;
